/**
 * Typed cell renderers for the DataTable.
 * Each renderer is React.memo'd for performance during column resize/sort.
 */
import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { formatDateInZone, parseInstant } from '../../lib/timezone';
import useDisplayZone from '../../hooks/useDisplayZone';
import { ExternalLink, Linkedin, Twitter, Facebook, Instagram, Globe } from 'lucide-react';
import DISCBadge from '../DISCBadge';
import { isTruthyCheckboxValue } from '../custom-fields/fieldTypes';

// ─── Text & Basic Types ─────────────────────────────────────────────────────

export const TextCell = React.memo(({ value }) => {
    const display = Array.isArray(value) ? value.join(', ') : (value || '—');
    return <span className="text-sm text-stone-700 truncate block" title={display}>{display}</span>;
});

export const NumberCell = React.memo(({ value }) => (
    <span className="text-sm text-stone-700 tabular-nums">{value != null ? Number(value).toLocaleString() : '—'}</span>
));

export const CurrencyCell = React.memo(({ value }) => {
    if (value == null) return <span className="text-stone-300">—</span>;
    const num = Number(value);
    let formatted;
    if (num >= 1e9) formatted = `$${(num / 1e9).toFixed(1)}B`;
    else if (num >= 1e6) formatted = `$${(num / 1e6).toFixed(1)}M`;
    else if (num >= 1e3) formatted = `$${(num / 1e3).toFixed(0)}K`;
    else formatted = `$${num.toLocaleString()}`;
    return <span className="text-sm text-stone-700 tabular-nums font-medium">{formatted}</span>;
});

// ─── Company Name ───────────────────────────────────────────────────────────

export const CompanyNameCell = React.memo(({ row }) => {
    const name = row.name || '';
    const domain = row.domain || row.city || '';
    return (
        <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-stone-100 flex items-center justify-center text-xs font-bold text-stone-600 flex-shrink-0">
                {row.logo_url ? <img src={row.logo_url} alt="" className="w-full h-full rounded-lg object-cover" /> : name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
                <span className="text-sm font-medium text-stone-900 truncate block">{name}</span>
                {domain && <span className="text-xs text-stone-400 truncate block">{domain}</span>}
            </div>
        </div>
    );
});

export const ContactNameCell = React.memo(({ row }) => {
    const name = `${row.first_name || ''} ${row.last_name || ''}`.trim();
    return (
        <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center text-xs font-bold text-stone-600 flex-shrink-0">
                {name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
                <span className="text-sm font-medium text-stone-900 truncate block">{name}</span>
                {row.email && <span className="text-xs text-stone-400 truncate block">{row.email}</span>}
            </div>
        </div>
    );
});

// ─── Badges & Status ────────────────────────────────────────────────────────

// Keep in sync with STATUSES in pages/Accounts.jsx — that list is the canonical
// account-status palette (it drives the status selector + filter dropdown), so a
// badge here must match the colour the user picked from. Every selectable status
// needs an entry: a missing one silently falls back to a grey badge, which is how
// form-captured `lead` accounts rendered before Issue #218.
const STATUS_COLORS = {
    target: 'bg-slate-100 text-slate-700', lead: 'bg-blue-50 text-blue-700',
    prospect: 'bg-indigo-50 text-indigo-700', opportunity: 'bg-violet-50 text-violet-700',
    customer: 'bg-emerald-50 text-emerald-700', churned: 'bg-red-50 text-red-700',
    partner: 'bg-stone-900 text-white',
};

export const StatusBadgeCell = React.memo(({ value }) => {
    if (!value) return <span className="text-stone-300">—</span>;
    return (
        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_COLORS[value] || 'bg-stone-100 text-stone-600'}`}>
            {value}
        </span>
    );
});

export const BadgeCell = React.memo(({ value }) => {
    if (!value) return <span className="text-stone-300">—</span>;
    return (
        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-stone-100 text-stone-700 capitalize">
            {value.replace(/_/g, ' ')}
        </span>
    );
});

// Canonical key personas (contact.key_role) — see backend services/pitchbook/roles.py
export const KEY_ROLE_LABELS = {
    ceo: 'CEO',
    cfo: 'CFO',
    general_manager: 'General Manager',
    ld_director: 'L&D Director',
    ld_manager: 'L&D Manager',
    ld_officer: 'L&D Officer',
};

export const KeyRoleCell = React.memo(({ value }) => {
    if (!value) return <span className="text-stone-300">—</span>;
    return (
        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-violet-50 text-violet-700">
            {KEY_ROLE_LABELS[value] || value.replace(/_/g, ' ')}
        </span>
    );
});

export const TagsCell = React.memo(({ value, colorMap }) => {
    if (!value || !Array.isArray(value) || value.length === 0) return <span className="text-stone-300">—</span>;
    return (
        <div className="flex flex-wrap gap-1">
            {value.slice(0, 3).map(tag => {
                const color = colorMap?.[tag.toLowerCase()]?.color;
                return (
                    <span key={tag}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-stone-100 text-stone-600"
                        style={color ? { borderLeft: `2px solid ${color}` } : {}}>
                        {tag}
                    </span>
                );
            })}
            {value.length > 3 && <span className="text-[10px] text-stone-400">+{value.length - 3}</span>}
        </div>
    );
});

// ─── Progress & Scores ──────────────────────────────────────────────────────

export const ProgressCell = React.memo(({ value }) => {
    if (value == null) return <span className="text-stone-300">—</span>;
    const score = Number(value);
    const color = score >= 70 ? 'bg-emerald-500' : score >= 40 ? 'bg-amber-500' : 'bg-red-500';
    return (
        <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-stone-100 rounded-full overflow-hidden max-w-[60px]">
                <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(score, 100)}%` }} />
            </div>
            <span className="text-xs text-stone-500 tabular-nums w-6">{score}</span>
        </div>
    );
});

// ─── Connection Strength ────────────────────────────────────────────────────

const STRENGTH_STYLES = {
    very_strong: { dot: 'bg-emerald-500', label: 'Very strong' },
    strong: { dot: 'bg-emerald-400', label: 'Strong' },
    weak: { dot: 'bg-amber-400', label: 'Weak' },
    very_weak: { dot: 'bg-red-400', label: 'Very weak' },
    none: { dot: 'bg-stone-300', label: 'No communication' },
    no_contact: { dot: 'bg-stone-200', label: 'No contact' },
};

export const ConnectionStrengthCell = React.memo(({ value }) => {
    const style = STRENGTH_STYLES[value] || STRENGTH_STYLES.none;
    return (
        <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${style.dot}`} />
            <span className="text-xs text-stone-600">{style.label}</span>
        </div>
    );
});

// ─── Relative Time ──────────────────────────────────────────────────────────

export const RelativeTimeCell = React.memo(({ value }) => {
    if (!value) return <span className="text-xs text-stone-300">No contact</span>;
    try {
        // parseInstant: an offset-less API string is a UTC instant, not
        // browser-local — otherwise "3 hours ago" is off by the viewer's offset.
        const date = parseInstant(value);
        if (!date) return <span className="text-xs text-stone-300">No contact</span>;
        return <span className="text-xs text-stone-500">{formatDistanceToNow(date, { addSuffix: true })}</span>;
    } catch {
        return <span className="text-xs text-stone-300">No contact</span>;
    }
});

export const DateCell = React.memo(({ value }) => {
    // #541 / #532 residue: absolute timestamps render via the shared
    // tz-aware formatter in the viewer's PROFILE zone (browser fallback when
    // no AuthProvider is mounted). The previous zone-less call rendered UTC.
    // Hook runs before the early return so hook order is stable.
    const zone = useDisplayZone();
    if (!value) return <span className="text-stone-300">—</span>;
    try {
        return <span className="text-xs text-stone-500">{formatDateInZone(value, { zone })}</span>;
    } catch {
        return <span className="text-stone-300">—</span>;
    }
});

// ─── URLs & Social ──────────────────────────────────────────────────────────

export const UrlCell = React.memo(({ value }) => {
    if (!value) return <span className="text-stone-300">—</span>;
    return (
        <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noopener noreferrer"
           className="text-xs text-stone-600 hover:text-stone-900 truncate flex items-center gap-1">
            {value.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]}
            <ExternalLink className="w-3 h-3 flex-shrink-0 opacity-50" />
        </a>
    );
});

const SOCIAL_ICONS = {
    linkedin: Linkedin, twitter: Twitter, facebook: Facebook, instagram: Instagram, angellist: Globe,
};

export const SocialUrlCell = React.memo(({ value, platform }) => {
    if (!value) return <span className="text-stone-300">—</span>;
    const Icon = SOCIAL_ICONS[platform] || Globe;
    const handle = value.split('/').pop() || value;
    return (
        <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noopener noreferrer"
           className="text-xs text-stone-500 hover:text-stone-900 flex items-center gap-1.5 truncate">
            <Icon className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="truncate">{handle}</span>
        </a>
    );
});

// ─── DISC ───────────────────────────────────────────────────────────────────

export const DISCCell = React.memo(({ value, row }) => {
    if (!value) return <span className="text-stone-300">—</span>;
    return <DISCBadge profile={value} secondary={row?.disc_secondary} size="sm" />;
});

// ─── Team / Avatar Group ────────────────────────────────────────────────────

export const AvatarGroupCell = React.memo(({ value, row }) => {
    // value = team_members array [{name, email, picture}] or team_member_ids
    const members = row?.team_members || [];
    if (members.length === 0 && !row?.owner_name) return <span className="text-xs text-stone-300">Unassigned</span>;

    const allPeople = [];
    if (row?.owner_name) {
        allPeople.push({ name: row.owner_name, isOwner: true });
    }
    members.forEach(m => {
        if (m.name && m.name !== row?.owner_name) allPeople.push(m);
    });

    return (
        <div className="flex items-center">
            <div className="flex -space-x-1.5">
                {allPeople.slice(0, 4).map((person, i) => (
                    <div
                        key={i}
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold border-2 border-white ${
                            person.isOwner ? 'bg-stone-900 text-white' : 'bg-stone-200 text-stone-600'
                        }`}
                        title={`${person.name}${person.isOwner ? ' (Owner)' : ''}`}
                    >
                        {person.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                ))}
                {allPeople.length > 4 && (
                    <div className="w-6 h-6 rounded-full bg-stone-100 text-stone-500 flex items-center justify-center text-[9px] font-medium border-2 border-white">
                        +{allPeople.length - 4}
                    </div>
                )}
            </div>
            {allPeople.length <= 2 && (
                <span className="ml-1.5 text-xs text-stone-500 truncate max-w-[80px]">
                    {allPeople.map(p => p.name?.split(' ')[0]).join(', ')}
                </span>
            )}
        </div>
    );
});

export const OwnerCell = React.memo(({ value, row, noOwnerLabel = 'Unassigned' }) => {
    const name = row?.owner_name || '';
    // #534: an owner_id that no longer resolves in this org (a member removed
    // from it keeps their account with org_id: None) is still an owner — show
    // that, not "Unassigned", on exactly the rows that need reassigning.
    if (!name && row?.owner_id) return <span className="text-xs italic text-stone-400">Former member</span>;
    // #864: contacts read "No Contact Owner" (their registry passes it); accounts keep "Unassigned".
    if (!name) return <span className="text-xs text-stone-300">{noOwnerLabel}</span>;
    return (
        <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-full bg-stone-900 text-white flex items-center justify-center text-[9px] font-bold flex-shrink-0">
                {name.charAt(0).toUpperCase()}
            </div>
            <span className="text-xs text-stone-600 truncate">{name}</span>
        </div>
    );
});

// ─── Cell Resolver ──────────────────────────────────────────────────────────

export function renderCell(type, value, row, fieldConfig = {}) {
    switch (type) {
        case 'company_name': return <CompanyNameCell row={row} />;
        case 'contact_name': return <ContactNameCell row={row} />;
        case 'text': return <TextCell value={value} />;
        case 'number': return <NumberCell value={value} />;
        case 'currency': return <CurrencyCell value={value} />;
        case 'url': return <UrlCell value={value} />;
        case 'date': return <DateCell value={value} />;
        case 'relative_time': return <RelativeTimeCell value={value} />;
        case 'tags': return <TagsCell value={value} />;
        case 'status_badge': return <StatusBadgeCell value={value} />;
        case 'badge': return <BadgeCell value={value} />;
        case 'key_role': return <KeyRoleCell value={value} />;
        case 'progress': return <ProgressCell value={value} />;
        case 'connection_strength': return <ConnectionStrengthCell value={value} />;
        case 'social': return <SocialUrlCell value={value} platform={fieldConfig.platform} />;
        case 'disc': return <DISCCell value={value} row={row} />;
        case 'team': return <AvatarGroupCell value={value} row={row} />;
        case 'owner': return <OwnerCell value={value} row={row} noOwnerLabel={fieldConfig.noOwnerLabel} />;
        // A custom Checkbox: legacy text such as "false" / "no" reads as unticked (#972 FE-11).
        case 'checkbox': return <TextCell value={value === null || value === undefined || value === '' ? '' : (isTruthyCheckboxValue(value) ? 'Yes' : 'No')} />;
        // 0 and false are values: a custom Checkbox or Number must not read as empty (#972).
        default: return <TextCell value={value === null || value === undefined ? '' : String(value)} />;
    }
}
